import { Box, Divider, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import "./vehicle-card.css";
import { VehicleMarkLogo } from "./vehicle-mark-logo";
import { DadosVeiculo } from "../../models/DadosVeiculoSimplesModel";
import { getDetailVehicle } from "../../apis/veiculo";
import { useWebgate } from "@xvia/webgate-connect";

type VehicleCardProps = {
  readonly placa: string;
  readonly renavam: number;
  readonly onClick?: () => void;
};

function VehicleCard({ placa, renavam, onClick }: VehicleCardProps) {
  const { accessToken } = useWebgate();

  const [veiculoDetalhe, setVeiculoDetalhe] = useState<DadosVeiculo>();

  const [countPendencies, setCountPendencies] = useState(0);

  const formattedModelName =
    veiculoDetalhe?.marcaModelo.descricao?.split("/")[1]?.trim() ||
    veiculoDetalhe?.marcaModelo.descricao ||
    "N/A";

  const computePendencies = useCallback((veiculo?: DadosVeiculo) => {
    if (!veiculo) return 0;
    const pendencyFlags = [
      veiculo.indicadorBloqueioLicenciamento,
      veiculo.indicadorBloqueioTransferencia,
      veiculo.indicadorBuscaApreensao,
      veiculo.indicadorChassiBloqueado,
      veiculo.indicadorPenhora,
      veiculo.indicadorRouboFurto,
      veiculo.indicadorVeiculoApreendido,
      veiculo.indicadorGravameLocalAtivo,
      veiculo.indicadorComunicacaoVendaAtiva,
      veiculo.indicadorIntencaoVenda,
      veiculo.indicadorPendenciaEmplacamento,
      veiculo.licenciamentoAtrasado,
    ];

    return pendencyFlags.filter(Boolean).length;
  }, []);

  useEffect(() => {
    const fetchVeiculos = async () => {
      const response = await getDetailVehicle(placa, renavam, accessToken);

      if (response.data) {
        setVeiculoDetalhe(response.data);
        setCountPendencies(computePendencies(response.data));
      }
    };

    if (accessToken) fetchVeiculos();
  }, [accessToken, placa, renavam, computePendencies]);

  const onLoad = useCallback(() => {
    window.open("/app/app-meus-veiculos", "_self");
  }, []);

  return (
    <Stack
      direction="column"
      alignItems="space-between"
      justifyContent="flex-start"
      sx={(theme) => ({
        width: "100%",
        backgroundColor: "#fff",
        borderRadius: "0.275rem",
        cursor: "pointer",
        overflow: "hidden",
        boxShadow: "1px 2px 4px 0px #ABABAB1A",
        border: "1px solid #E0E3EB",
      })}
      onClick={onLoad}
    >
      {/* Label Situation */}
      <Stack
        direction="row"
        justifyContent="center"
        sx={(theme) => ({
          width: "100%",
          backgroundColor: countPendencies > 0 ? "#FDB913" : "#007932",
          color: "#fff",
          p: 1,
        })}
      >
        <Typography align="center" fontWeight={700}>
          {countPendencies > 0
            ? "Veículo pendente de regularização"
            : "Seu veículo está regularizado"}
        </Typography>
      </Stack>
      {/* Info */}
      <Stack direction="column" sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={(theme) => ({ width: "100%" })}
        >
          <Box sx={{ width: "2.5rem" }}>
            {veiculoDetalhe?.marcaModelo.descricao && (
              <VehicleMarkLogo
                mark={veiculoDetalhe?.marcaModelo.descricao}
                type={veiculoDetalhe?.marcaModelo.id}
              />
            )}
          </Box>

          <Stack direction="column" alignItems="flex-end">
            <Typography sx={{ color: "var(--xvia-color-text-secondary)" }}>
              Modelo
            </Typography>
            <Typography
              sx={{
                color: countPendencies > 0 ? "#FDB913" : "#007932",
                fontWeight: 500,
              }}
            >
              {formattedModelName}
            </Typography>
          </Stack>
        </Stack>
        <Divider sx={{ my: 1, width: "100%", paddingX: "2rem" }}></Divider>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={(theme) => ({ width: "100%" })}
        >
          <Stack direction="column">
            <Typography sx={{ color: "var(--xvia-color-text-secondary)" }}>
              Licenciamento
            </Typography>
            <Typography
              sx={{
                color: countPendencies > 0 ? "#FDB913" : "#007932",
                fontWeight: 500,
              }}
            >
              {veiculoDetalhe?.anoUltimoLicenciamento || "N/A"}
            </Typography>
          </Stack>
          <Stack direction="column" alignItems="flex-end">
            <Typography sx={{ color: "var(--xvia-color-text-secondary)" }}>
              Placa
            </Typography>
            <Typography
              sx={{
                color: countPendencies > 0 ? "#FDB913" : "#007932",
                fontWeight: 500,
              }}
            >
              {veiculoDetalhe?.placa || "N/A"}
            </Typography>
          </Stack>
        </Stack>
        <Divider sx={{ my: 1, width: "100%", paddingX: "2rem" }}></Divider>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={(theme) => ({ width: "100%" })}
        >
          <Stack direction="column">
            <Typography sx={{ color: "var(--xvia-color-text-secondary)" }}>
              Renavam
            </Typography>
            <Typography
              sx={{
                color: countPendencies > 0 ? "#FDB913" : "#007932",
                fontWeight: 500,
              }}
            >
              {veiculoDetalhe?.renavam || "N/A"}
            </Typography>
          </Stack>
          <Stack direction="column" alignItems="flex-end">
            <Typography sx={{ color: "var(--xvia-color-text-secondary)" }}>
              Pendências
            </Typography>
            <Typography
              sx={{
                color: countPendencies > 0 ? "#FDB913" : "#007932",
                fontWeight: 500,
              }}
            >
              {countPendencies}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default VehicleCard;
